/**
 * Shared helpers for account-alert "announcements" (dashboard banner + archive).
 *
 * Announcement start/stop dates are stored in Pacific time (legacy convention);
 * activity is evaluated against the viewer's timezone.
 */
import { pacificToWallClock, wallClockPartsInTz } from '@/lib/facilityTimezone';
import type { AccountAlert } from '@/services/accountAlerts.service';

const ZERO_DATE = '0000-00-00 00:00:00';

function getNowInTz(tz: string): string {
  const now = new Date();
  // A zone that can't be read falls back to Pacific, where pacificToWallClock
  // leaves the start/stop bounds in the same case, so both sides stay in one zone.
  const p = wallClockPartsInTz(now, tz) ?? wallClockPartsInTz(now, 'America/Los_Angeles');
  if (!p) return now.toISOString().slice(0, 19).replace('T', ' ');

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

/** True when the announcement is inside its start/stop window for the viewer. */
export function isAlertActive(alert: AccountAlert, userTz: string): boolean {
  const now = getNowInTz(userTz);

  if (alert.startDate && alert.startDate !== ZERO_DATE) {
    const pacDate = alert.startDate.slice(0, 10);
    const pacTime = alert.startDate.slice(11, 16) || '00:00';
    const { date, time } = pacificToWallClock(pacDate, pacTime, userTz);
    if (now < `${date} ${time}:00`) return false;
  }

  if (alert.stopDate && alert.stopDate !== ZERO_DATE) {
    const pacDate = alert.stopDate.slice(0, 10);
    const pacTime = alert.stopDate.slice(11, 16) || '23:59';
    const { date, time } = pacificToWallClock(pacDate, pacTime, userTz);
    if (now > `${date} ${time}:59`) return false;
  }

  return true;
}

/** Human date for an announcement (prefers createDate, falls back to startDate). */
export function formatAnnouncementDate(alert: AccountAlert): string | null {
  const raw =
    alert.createDate && alert.createDate !== ZERO_DATE
      ? alert.createDate
      : alert.startDate && alert.startDate !== ZERO_DATE
        ? alert.startDate
        : '';
  if (!raw) return null;
  const d = new Date(`${raw.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
